import React, { useCallback } from "react";
import type { Icon as IconType } from "../types";
import { getMediaCentralUrl } from "../services/media-service";
import { BackButton, CloseButton } from "./ui";

export interface SheetHeaderProps {
  title: string;
  description?: string;
  icon?: IconType;
  onClose: () => void;
  onBack?: () => void;
  mode?: "default" | "certification-detail";
  className?: string;
  autoFocusBack?: boolean;
}

export const SheetHeader: React.FC<SheetHeaderProps> = ({
  title,
  description,
  icon,
  onClose,
  onBack,
  mode = "default",
  className = "",
  autoFocusBack = false,
}) => {
  // Ref callback to focus back button when it renders
  const backButtonRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (node && autoFocusBack) {
        node.focus({ preventScroll: true });
      }
    },
    [autoFocusBack],
  );

  const renderBackButton = () =>
    onBack && (
      <div className="sheet-header__back">
        <BackButton
          onClick={onBack}
          ariaLabel="Go back to features list"
          ref={backButtonRef}
        />
      </div>
    );

  const renderIcon = () =>
    icon && (
      <div className="sheet-header__icon">
        <img
          src={getMediaCentralUrl(icon.physicalId, icon.extension)}
          alt=""
          className="sheet-header__leaf-icon"
          aria-hidden="true"
          width={icon.width}
          height={icon.height}
        />
      </div>
    );

  const renderTitleSection = () => (
    <div className="sheet-header__title-section">
      <h2 className="sheet-header__title">
        {mode === "certification-detail" ? "Certification Details" : title}
      </h2>
    </div>
  );

  const renderCloseButton = () => (
    <div className="sheet-header__actions">
      <CloseButton
        onClick={onClose}
        ariaLabel="Close sustainability features"
      />
    </div>
  );

  const renderDescription = () => (
    <div className="sheet-header__description">{description}</div>
  );

  return (
    <div
      className={`sheet-header ${mode === "certification-detail" ? "sheet-header--certification-detail" : ""} ${className}`}
    >
      <div className="sheet-header__content">
        {mode === "certification-detail" ? renderBackButton() : renderIcon()}

        {renderTitleSection()}
        {renderCloseButton()}
      </div>

      {mode === "default" && description && renderDescription()}
    </div>
  );
};
